import React from "react";
import {
  Lightbulb,
  ClipboardList,
  BarChart3,
  Code,
  Settings,
  CloudUpload,
  Headset,
  ChevronRight,
} from "lucide-react";
import "./ResearchToProduction.css";

const steps = [
  {
    number: 1,
    name: "Research",
    icon: Lightbulb,
    color: "#3b82f6", // Blue
    gradient: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
    desc: "Conducting deep market research, domain analysis, and technical feasibility studies to define solution requirements.",
  },
  {
    number: 2,
    name: "Planning",
    icon: ClipboardList,
    color: "#16a34a", // Green
    gradient: "linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)",
    desc: "Creating architecture roadmaps, project timelines, sprint milestones, and resource allocation plans.",
  },
  {
    number: 3,
    name: "Design",
    icon: BarChart3, // Teal
    color: "#06b6d4",
    gradient: "linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)",
    desc: "Crafting wireframes, UI/UX designs, system schemas, and interactive user experience prototypes.",
  },
  {
    number: 4,
    name: "Development",
    icon: Code, // Light Green
    color: "#84cc16",
    gradient: "linear-gradient(135deg, #f7fee7 0%, #ecfccb 100%)",
    desc: "Engineering clean, modular source code, integrating APIs, cloud infrastructure, and core business logic.",
  },
  {
    number: 5,
    name: "Testing",
    icon: Settings, // Yellow
    color: "#eab308",
    gradient: "linear-gradient(135deg, #fefce8 0%, #fef9c3 100%)",
    desc: "Performing automated unit tests, QA validation, security audits, and user acceptance testing (UAT).",
  },
  {
    number: 6,
    name: "Deployment",
    icon: CloudUpload, // Orange
    color: "#ea580c",
    gradient: "linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)",
    desc: "Executing automated CI/CD pipeline deployments to live production environments with zero downtime.",
  },
  {
    number: 7,
    name: "Support",
    icon: Headset, // Slate/Gray
    color: "#64748b",
    gradient: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
    desc: "Providing 24/7 continuous monitoring, system performance optimization, updates, and SLA support.",
  },
];

function ResearchToProduction() {
  return (
    <div className="research-to-production">
      <div className="research-to-production-wrapper">
        <div className="header section-header">
          <h1 className="title section-title">Research to Production</h1>
          <p className="research-description section-description">
            Experience how our AI-powered features simplify workflows, automate
            routine tasks, and help your team achieve more—so you can focus on
            growing your business.
          </p>
        </div>

        <div className="flow-container">
          {steps.map((step, index) => {
            const StepIcon = step.icon;
            return (
              <React.Fragment key={step.number}>
                <div className="step-item">
                  {/* Authentic SVG Cloud Speech Bubble Popup matching Image 1 */}
                  <div className={`cloud-popup-card ${index % 2 === 1 ? "cloud-down" : "cloud-up"}`}>
                    <svg
                      className="cloud-bg-svg"
                      viewBox="0 0 260 160"
                      preserveAspectRatio="none"
                    >
                      <path
                        d="M 45 125 C 25 125 10 110 10 90 C 0 75 10 50 30 40 C 25 20 50 5 80 15 C 100 0 135 0 160 15 C 185 5 215 15 225 35 C 245 40 260 60 255 85 C 265 105 250 125 225 125 C 205 130 185 125 155 125 L 130 152 L 115 125 Z"
                        fill="#ffffff"
                        stroke="#e2e8f0"
                        strokeWidth="1.5"
                        filter="drop-shadow(0px 8px 24px rgba(15, 23, 42, 0.14))"
                      />
                    </svg>

                    <div className="cloud-content-text">
                      <strong className="cloud-title" style={{ color: step.color }}>
                        {step.name}
                      </strong>
                      <p className="cloud-desc">{step.desc}</p>
                    </div>
                  </div>

                  <span className="step-number" style={{ color: step.color }}>
                    {step.number}
                  </span>
                  
                  <div
                    className="step-circle"
                    style={{
                      borderColor: step.color,
                      "--step-hover-bg": step.gradient,
                      "--step-hover-shadow": `${step.color}25`,
                    }}
                  >
                    <StepIcon
                      className="step-icon"
                      strokeWidth={1.5}
                      style={{ color: step.color }}
                    />
                  </div>

                  <span className="step-text">{step.name}</span>
                </div>

                {index < steps.length - 1 && (
                  <div
                    className="flow-connector"
                    style={{
                      "--connector-start": step.color,
                      "--connector-end": steps[index + 1].color,
                    }}
                  >
                    <div
                      className="connector-arrow-circle"
                      style={{
                        borderColor: steps[index + 1].color,
                        color: steps[index + 1].color,
                      }}
                    >
                      <ChevronRight className="connector-arrow-icon" />
                    </div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default ResearchToProduction;
